import { parseTenThousandths } from '@/shared/utils/moneyArithmetic';
import {
    leftToPayTooLowReason,
    MOTORPLAN_CHECKING_REASON,
    MOTORPLAN_NO_MODE_REASON,
    MOTORPLAN_PAID_IN_FULL_REASON,
    MOTORPLAN_UNAVAILABLE_REASON,
} from './motorplanPaymentCopy';
import type { LeftToPay } from '../types/paymentLinks.types';

export type MotorplanAvailability =
    | { available: false; reason: string }
    | { available: true; partPayment: boolean; payOff: boolean };

/** Elemis refuses a part payment that leaves under £1.00 owed, in ten-thousandths. */
export const MIN_REMAINING_SCALED = 10_000n;

const unavailable = (reason: string): MotorplanAvailability => ({ available: false, reason });

/** The most a part payment may be, in pence; negative when none is possible. */
export function partPaymentMaxMinor(leftToPay: string): number {
    const scaled = parseTenThousandths(leftToPay);
    if (scaled === null || scaled < MIN_REMAINING_SCALED) return -1;
    return Number((scaled - MIN_REMAINING_SCALED) / 100n);
}

export function motorplanAvailability(
    leftToPay: LeftToPay | undefined,
    isLoading: boolean,
    shortfallScaled: number,
    payOffAllowed: boolean,
): MotorplanAvailability {
    if (isLoading) return unavailable(MOTORPLAN_CHECKING_REASON);
    if (leftToPay === undefined) return unavailable(MOTORPLAN_UNAVAILABLE_REASON);
    if (!leftToPay.eligible) {
        return unavailable(leftToPay.reason === 'paid_in_full' ? MOTORPLAN_PAID_IN_FULL_REASON : MOTORPLAN_UNAVAILABLE_REASON);
    }

    const left = parseTenThousandths(leftToPay.leftToPay);
    if (left === null) return unavailable(MOTORPLAN_UNAVAILABLE_REASON);
    const shortfall = BigInt(Math.ceil(shortfallScaled));
    if (left < shortfall) return unavailable(leftToPayTooLowReason(leftToPay.leftToPay));

    const partPayment = left - MIN_REMAINING_SCALED >= shortfall;
    if (!partPayment && !payOffAllowed) return unavailable(MOTORPLAN_NO_MODE_REASON);
    return { available: true, partPayment, payOff: payOffAllowed };
}
